import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { mediaSrc } from "../../lib/api";
import { Icon } from "../../lib/icons";
import { RichText } from "../site/Blocks";

export const Benefits = ({ data }) => (
  <section className="bg-sand py-10" data-testid="home-benefits">
    <ul className="container-site grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-0">
      {data.items.map((b, i) => (
        <li key={b.title} className={`px-6 text-center ${i ? "lg:border-l lg:border-slate-200" : ""}`} data-testid={`benefit-${i}`}>
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#FCE8E9] text-burgundy">
            <Icon name={b.icon} size={28} />
          </span>
          <h3 className="mt-3 text-xl font-bold text-navy">{b.title}</h3>
          <p className="mx-auto mt-1 max-w-[16rem] text-[#4A5568]">{b.text}</p>
        </li>
      ))}
    </ul>
  </section>
);

function toEmbed(url) {
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{6,})/);
  if (yt) return { type: "iframe", src: `https://www.youtube-nocookie.com/embed/${yt[1]}` };
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return { type: "iframe", src: `https://player.vimeo.com/video/${vm[1]}` };
  return { type: "video", src: mediaSrc(url) };
}

export function AboutEvent({ data }) {
  const [open, setOpen] = useState(false);
  const video = data.video_url ? toEmbed(data.video_url) : null;
  return (
    <section className="py-16 lg:py-20" data-testid="home-about">
      <div className="container-site grid items-center gap-12 lg:grid-cols-2">
        <div>
          <p className="eyebrow">{data.eyebrow}</p>
          <h2 className="mt-5 text-3xl text-navy sm:text-4xl lg:text-[2.6rem]">{data.heading}</h2>
          <RichText html={data.body} className="mt-5" />
          <Link to={data.button_link || "/event"} className="btn-primary mt-2" data-testid="about-learn-more-btn">
            {data.button_label} <ArrowRight size={18} aria-hidden="true" />
          </Link>
        </div>
        <figure className="overflow-hidden rounded-lg shadow-sm">
          <div className="relative aspect-[16/9]">
            <img src={mediaSrc(data.image_url)} alt={data.image_alt || ""} className="h-full w-full object-cover" />
            {video && (
              <button type="button" onClick={() => setOpen(true)} aria-label="Play video: Discover CIArb Bahamas" data-testid="about-play-video-btn"
                className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-navy shadow-lg transition-transform duration-200 hover:scale-105">
                <Play size={26} fill="currentColor" aria-hidden="true" />
              </button>
            )}
          </div>
          <figcaption className="flex items-center justify-between bg-navy px-5 py-4 text-white">
            <span className="font-serif text-lg">{data.caption_title}</span>
            {video && (
              <button type="button" onClick={() => setOpen(true)} className="on-dark flex items-center gap-2 text-xs font-semibold tracking-[0.2em]" data-testid="about-watch-video-btn">
                WATCH VIDEO <ArrowRight size={14} aria-hidden="true" />
              </button>
            )}
          </figcaption>
        </figure>
      </div>
      {video && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-4xl p-0" data-testid="video-dialog">
            <DialogTitle className="sr-only">{data.caption_title}</DialogTitle>
            {open && (video.type === "iframe"
              ? <iframe title={data.caption_title} src={video.src} className="aspect-video w-full" allow="autoplay; fullscreen" allowFullScreen />
              : <video src={video.src} controls className="w-full" />)}
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
}

export const Stats = ({ data }) => (
  <section className="bg-paleblue py-10" data-testid="home-stats">
    <ul className="container-site grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-0">
      {data.items.map((s, i) => (
        <li key={s.label} className={`flex items-center justify-center gap-4 px-4 ${i ? "lg:border-l lg:border-slate-300" : ""}`} data-testid={`stat-${i}`}>
          <Icon name={s.icon} size={40} strokeWidth={1.4} className="shrink-0 text-navy" />
          <div>
            <p className={`font-serif font-bold text-navy ${s.value.length > 8 ? "text-lg leading-tight" : "text-3xl"}`}>{s.value}</p>
            <p className="text-[#4A5568]">{s.label}</p>
          </div>
        </li>
      ))}
    </ul>
    {data.unverified && data.note && <p className="container-site mt-6 text-center text-sm text-[#4A5568]" data-testid="stats-unverified-note">{data.note}</p>}
  </section>
);
